import { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';

import LoadStatus from 'config/load_status';

import editRecipe from 'domain/recipe/usecases/edit_recipe';
import areRecipesEqual from 'domain/recipe/usecases/are_recipes_equal';

import delayed from 'utils/delayed';

const useEditRecipeForm = ({ id, recipeContent, onRecipeSaved = () => {} }) => {
  if (!id) throw new Error('id cannot be empty');

  const { enqueueSnackbar } = useSnackbar();

  const [editedRecipeContent, setEditedRecipeContent] = useState(recipeContent);
  const [editRecipeStatus, setEditRecipeStatus] = useState(null);
  const [hasRecipeChanged, setHasRecipeChanged] = useState(false);

  useEffect(() => {
    setHasRecipeChanged(!areRecipesEqual(editedRecipeContent, recipeContent));
  }, [editedRecipeContent, recipeContent]);

  useEffect(() => {
    if (editRecipeStatus !== LoadStatus.LOADING) return;

    const edit = async () => {
      try {
        await delayed(
          editRecipe(
            id,
            editedRecipeContent,
            recipeContent.additionalData.coverPhotoURL
          )
        );
        setEditRecipeStatus(LoadStatus.DONE);
        onRecipeSaved(editedRecipeContent);
        enqueueSnackbar('Recipe was edited succesfully', {
          variant: 'success',
        });
      } catch (error) {
        enqueueSnackbar('Recipe edition failed', { variant: 'error' });
        setEditRecipeStatus(LoadStatus.ERROR);
        throw error;
      }
    };
    edit();
  }, [
    editRecipeStatus,
    editedRecipeContent,
    enqueueSnackbar,
    id,
    onRecipeSaved,
    recipeContent.additionalData.coverPhotoURL,
  ]);

  const saveRecipe = () => {
    if (editRecipeStatus === LoadStatus.LOADING) return;
    setEditRecipeStatus(LoadStatus.LOADING);
  };

  return {
    editedRecipeContent,
    setEditedRecipeContent,

    saveRecipe,
    hasRecipeChanged,

    isEditing: editRecipeStatus === LoadStatus.LOADING,
  };
};

export default useEditRecipeForm;
